'use client';

import { c as _c } from "react/compiler-runtime";
import type { Address } from 'viem';
import { ChainUtils } from '@morpho-org/blue-sdk';
import { ExternalLink, TableNoResults } from '@morpho-org/ui-v6/server';
import { Button } from '@repo/ui';
import { ArrowDiagonalUp } from '@repo/ui/icons';
import { useState } from 'react';
import type { OracleDecodingOutcome } from '@/lib/shared/oracleDecoding';
import { ErrorDataUnavailable } from '@/components/common/Error';
import { SubSectionTitle } from '@/components/common/SubSectionTitle';
import { TableScrollArea } from '@/components/common/Table/CompactTable/Table';
import { ORACLE_METHODOLOGY_URL } from '@/config/external-urls';
import type { OracleFeeds, Side } from './oracleFeeds';
import { groupByBranch } from './groupBySide';
import { Attribution } from './OracleFeedParts';
import { credit } from './oracleFeeds';
import { OracleFeedsCards } from './OracleFeedsCards';
import { OracleFeedsTable } from './OracleFeedsTable';
export type OracleFeedsState = OracleFeeds | null | 'error';
function Title() {
  const $ = _c(1);
  let t0;
  if ($[0] === Symbol.for("react.memo_cache_sentinel")) {
    t0 = <SubSectionTitle>Oracle price construction</SubSectionTitle>;
    $[0] = t0;
  } else {
    t0 = $[0];
  }
  return t0;
}
export const EMPTY_STATE = {
  generic: 'This oracle has not been decoded. You can review its contract on the explorer.',
  pending: "This oracle hasn't been decoded yet. You can review its contract on the explorer."
};

/** The copy for the API's `outcome` (API-4888). */
export function emptyStateText(outcome: OracleDecodingOutcome | null | undefined) {
  switch (outcome) {
    case 'pending':
      return EMPTY_STATE.pending;
    default:
      return EMPTY_STATE.generic;
  }
}

/** Empty state with a contract link and the methodology footer. */
function FeedsEmpty(t0) {
  const $ = _c(15);
  const {
    chainId,
    oracle,
    outcome
  } = t0;
  let t1;
  if ($[0] !== chainId || $[1] !== oracle) {
    t1 = ChainUtils.getExplorerAddressUrl(chainId, oracle);
    $[0] = chainId;
    $[1] = oracle;
    $[2] = t1;
  } else {
    t1 = $[2];
  }
  const explorer = t1;
  let t2;
  if ($[3] !== outcome) {
    t2 = emptyStateText(outcome);
    $[3] = outcome;
    $[4] = t2;
  } else {
    t2 = $[4];
  }
  let t3;
  if ($[5] !== explorer) {
    t3 = explorer && <Button asChild={true} variant="secondary" size="sm"><ExternalLink href={explorer}>View oracle contract<ArrowDiagonalUp /></ExternalLink></Button>;
    $[5] = explorer;
    $[6] = t3;
  } else {
    t3 = $[6];
  }
  let t4;
  if ($[7] !== t3) {
    t4 = <div className="gap-md flex flex-wrap items-center justify-center">{t3}</div>;
    $[7] = t3;
    $[8] = t4;
  } else {
    t4 = $[8];
  }
  let t5;
  if ($[9] !== t2 || $[10] !== t4) {
    t5 = <TableScrollArea className="w-full rounded-[20px]"><TableNoResults text={t2} height="auto" className="px-md py-xl rounded-none text-center">{t4}</TableNoResults></TableScrollArea>;
    $[9] = t2;
    $[10] = t4;
    $[11] = t5;
  } else {
    t5 = $[11];
  }
  let t6;
  if ($[12] === Symbol.for("react.memo_cache_sentinel")) {
    t6 = <div className="mt-xs gap-x-md gap-y-2xs text-muted-foreground text-2xs flex flex-col md:flex-row md:items-baseline md:justify-between"><span className="md:ml-auto"><ExternalLink href={ORACLE_METHODOLOGY_URL} className="gap-2xs inline-flex items-center text-inherit">View oracle decoding methodology<ArrowDiagonalUp className="size-3" /></ExternalLink></span></div>;
    $[12] = t6;
  } else {
    t6 = $[12];
  }
  let t7;
  if ($[13] !== t5) {
    t7 = <>{t5}{t6}</>;
    $[13] = t5;
    $[14] = t7;
  } else {
    t7 = $[14];
  }
  return t7;
}
export function OraclePriceConstruction(t0) {
  const $ = _c(25);
  const {
    chainId,
    oracle,
    feeds,
    symbols,
    logos,
    outcome
  } = t0;
  let t1;
  if ($[0] === Symbol.for("react.memo_cache_sentinel")) {
    t1 = new Set();
    $[0] = t1;
  } else {
    t1 = $[0];
  }
  const [openIds, setOpenIds] = useState(t1);
  if (feeds === "error") {
    let t2;
    if ($[1] === Symbol.for("react.memo_cache_sentinel")) {
      t2 = <div className="gap-sm flex w-full flex-col"><Title /><ErrorDataUnavailable minHeight="132px" /></div>;
      $[1] = t2;
    } else {
      t2 = $[1];
    }
    return t2;
  }
  if (feeds === null) {
    let t2;
    if ($[2] === Symbol.for("react.memo_cache_sentinel")) {
      t2 = <Title />;
      $[2] = t2;
    } else {
      t2 = $[2];
    }
    let t3;
    if ($[3] !== chainId || $[4] !== oracle || $[5] !== outcome) {
      t3 = <div className="gap-sm flex w-full flex-col">{t2}<FeedsEmpty chainId={chainId} oracle={oracle} outcome={outcome} /></div>;
      $[3] = chainId;
      $[4] = oracle;
      $[5] = outcome;
      $[6] = t3;
    } else {
      t3 = $[6];
    }
    return t3;
  }
  let t2;
  let t3;
  let t4;
  let t5;
  if ($[7] !== chainId || $[8] !== feeds || $[9] !== logos || $[10] !== openIds || $[11] !== symbols) {
    const groups = groupByBranch(feeds, symbols, logos);
    let t6;
    if ($[16] === Symbol.for("react.memo_cache_sentinel")) {
      t6 = (id, open) => setOpenIds(prev => {
        const next = new Set(prev);
        if (open) {
          next.add(id);
        } else {
          next.delete(id);
        }
        return next;
      });
      $[16] = t6;
    } else {
      t6 = $[16];
    }
    const toggle = t6;
    t4 = "gap-sm flex w-full flex-col";
    if ($[17] === Symbol.for("react.memo_cache_sentinel")) {
      t5 = <Title />;
      $[17] = t5;
    } else {
      t5 = $[17];
    }
    t2 = "gap-lg flex flex-col";
    t3 = groups.map((branch, index) => {
      const attribution = <Attribution providers={credit([...branch.oracleRows, ...branch.sides.flatMap(_temp)]).providers} observedAt={feeds.observedAt} />;
      return <section key={branch.id} className="gap-sm flex flex-col">{branch.title && <h4 className="text-foreground text-xs font-medium">{branch.title}</h4>}<div className="hidden md:block"><OracleFeedsTable chainId={chainId} branch={branch} /></div><div className="[contain:inline-size] md:hidden"><OracleFeedsCards chainId={chainId} branch={branch} openIds={openIds} onToggle={toggle} /></div>{index < groups.length - 1 ? <div className="mt-xs">{attribution}</div> : <div className="mt-xs gap-x-md gap-y-2xs text-muted-foreground text-2xs flex flex-col md:flex-row md:items-baseline md:justify-between">{attribution}<span className="md:ml-auto"><ExternalLink href={ORACLE_METHODOLOGY_URL} className="gap-2xs inline-flex items-center text-inherit">View oracle decoding methodology<ArrowDiagonalUp className="size-3" /></ExternalLink></span></div>}</section>;
    });
    $[7] = chainId;
    $[8] = feeds;
    $[9] = logos;
    $[10] = openIds;
    $[11] = symbols;
    $[12] = t2;
    $[13] = t3;
    $[14] = t4;
    $[15] = t5;
  } else {
    t2 = $[12];
    t3 = $[13];
    t4 = $[14];
    t5 = $[15];
  }
  let t6;
  if ($[18] !== t2 || $[19] !== t3) {
    t6 = <div className={t2}>{t3}</div>;
    $[18] = t2;
    $[19] = t3;
    $[20] = t6;
  } else {
    t6 = $[20];
  }
  let t7;
  if ($[21] !== t4 || $[22] !== t5 || $[23] !== t6) {
    t7 = <div className={t4}>{t5}{t6}</div>;
    $[21] = t4;
    $[22] = t5;
    $[23] = t6;
    $[24] = t7;
  } else {
    t7 = $[24];
  }
  return t7;
}
function _temp(side) {
  return side.rows;
}